import { ethers } from "ethers";
import { getContracts } from "../utils/getContracts";
import { NETWORKS } from "../utils/config";

// Types for better type safety
interface CreateListingParams {
  energyType: string;
  excessEnergyKW: number;
  pricePerUnit: number;
  location: string;
  minPurchaseAmount: number;
  maxPurchaseAmount: number;
}

interface ErrorInfo {
  code: string;
  message: string;
  details?: any;
}

// Custom Error Classes
class EnergyTradingError extends Error {
  constructor(public code: string, message: string, public details?: any) {
    super(message);
    this.name = "EnergyTradingError";
  }
}

// ABI fragments for error decoding - include both router and energy sharing contract errors
const ERROR_ABI = [
  // Custom errors from EnergySharingContract and BuyerDemandContract (deduplicated)
  "error InvalidPriceRange()",
  "error InvalidPurchaseLimits()",
  "error InvalidAddress()",
  "error InvalidPositiveValue()",
  "error MinPriceMustBeLessThanMax()",
  "error NotListingOwner()",
  "error NotTradeBuyer()",
  "error NotTradeSeller()",
  "error NotRouter()",
  "error SamePrice()",
  "error InsufficientExcessEnergy()",
  "error SellerNotRegistered()",
  "error InvalidListing()",
  "error BuyerNotRegistered()",
  "error InvalidPurchaseAmount()",
  "error InsufficientCapacity()",
  "error InvalidTrade()",
  "error InvalidTradeState()",
  "error ListingNotActive()",
  "error ListingNotPaused()",
  "error ListingNotFound()",
  "error OffsetOutOfBounds()",
  "error BuyerUsageExceedsSellerExcess()",
  "error InvalidSellLimits()",
  "error NotDemandOwner()",
  "error DemandNotFound()",
  "error DemandNotActive()",
  "error DemandNotPaused()",
  "error TradeNotFound()",
  "error TradeNotPending()",
  "error PaymentNotConfirmed()",
  "error InvalidDemand()",
  "error BelowMinSell()",
  "error ExceedsMaxSell()",
  "error InsufficientDemandedUnits()",
  "error MaxSellExceedsQuantity()",
  "error InvalidMinSellAmount()",
];

// Router Contract ABI (full, based on NewEnergyRouterABI.json)
const ROUTER_ABI = [
  // Events
  "event DemandAndOrderCancelled(uint256 indexed demandId, uint256 indexed orderId, address indexed buyer)",
  "event DemandTradeInitiated(uint256 tradeId, uint256 demandId, address seller, uint256 units, uint256 price)",
  "event EnergyContractsSet(address energySharingContract, address buyerDemandContract)",
  "event ExcessEnergyCredited(address indexed user, uint256 amount)",
  "event GrantRole(bytes32 indexed role, address indexed account, bool status, uint256 timestamp)",
  "event Initialized(uint8 version)",
  "event ListingAndOrderCancelled(uint256 indexed listingId, uint256 indexed orderId, address indexed seller)",
  "event ListingTradeInitiated(uint256 tradeId, uint256 listingId, address buyer, uint256 units, uint256 price)",
  "event OwnershipTransferred(address indexed previousOwner, address indexed newOwner)",
  // Functions
  "function ADMIN_ROLE() view returns (bytes32)",
  "function buyerDemandContract() view returns (address)",
  "function cancelDemand(uint256 demandId)",
  "function cancelDemandTrade(uint256 tradeId)",
  "function cancelDemandWithOrder(uint256 demandId, uint256 orderId)",
  "function cancelListing(uint256 listingId)",
  "function cancelListingTrade(uint256 tradeId)",
  "function cancelListingWithOrder(uint256 listingId, uint256 orderId)",
  "function completeDemandTrade(uint256 tradeId)",
  "function completeListingTrade(uint256 tradeId)",
  "function confirmDemandTradePayment(uint256 tradeId)",
  "function confirmListingTradePayment(uint256 tradeId)",
  "function consumeEnergyUnits(address user, uint256 newBalance)",
  "function createDemandWithOrder(string energyType, uint256 quantity, uint256 pricePerUnit, bytes32 location, uint256 minSellAmount, uint256 maxSellAmount)",
  "function createListingWithOrder(bytes32 energyType, uint256 excessEnergyKW, uint256 pricePerUnit, bytes32 location, uint256 minPurchaseAmount, uint256 maxPurchaseAmount)",
  "function creditUserExcessEnergy(address seller, uint256 excessEnergyKW)",
  "function energySharingContract() view returns (address)",
  "function getActiveDemands(uint256 offset, uint256 limit) view returns (tuple(uint256 demandId, address buyer, bytes32 energyType, bytes32 userId, uint256 quantity, uint256 pricePerUnit, bytes32 location, uint256 minSellAmount, uint256 maxSellAmount, uint8 status)[] paginatedDemands, uint256 activeCount)",
  "function getActiveListings(uint256 offset, uint256 limit) view returns (tuple(uint256 listingId, address seller, bytes32 energyType, bytes32 userId, uint256 quantity, uint256 pricePerUnit, bytes32 location, uint256 minPurchaseAmount, uint256 maxPurchaseAmount, uint8 status)[] paginatedListings, uint256 activeCount)",
  "function getAllListings(uint256 offset, uint256 limit) view returns (tuple(uint256 listingId, address seller, bytes32 energyType, bytes32 userId, uint256 quantity, uint256 pricePerUnit, bytes32 location, uint256 minPurchaseAmount, uint256 maxPurchaseAmount, uint8 status)[] paginatedListings, uint256 totalCount)",
  "function getCounters() view returns (uint256 listingCounter, uint256 tradeCounter)",
  "function getDeliveredEnergyUnits(address buyer) view returns (uint256)",
  "function getDemandCounters() view returns (uint256 demandCounter, uint256 tradeCounter)",
  "function getDemandTradeCount() view returns (uint256)",
  "function getEnergyListingTradeCount() view returns (uint256)",
  "function getEnergyUnitBalance(address user) view returns (uint256)",
  "function getExcessEnergyBalance(address seller) view returns (uint256)",
  "function getListingPricePerUnit(uint256 listingId) view returns (uint256)",
  "function getMaxPrice() view returns (uint256)",
  "function getMinPrice() view returns (uint256)",
  "function getOrderBook(bytes32 location) view returns (tuple(uint256 orderId, address trader, bool isBuyOrder, uint256 minPrice, uint256 maxPrice, uint256 quantity, uint256 filledQuantity, bytes32 location, uint256 timestamp, bool isActive, uint256 expiry)[] bids, tuple(uint256 orderId, address trader, bool isBuyOrder, uint256 minPrice, uint256 maxPrice, uint256 quantity, uint256 filledQuantity, bytes32 location, uint256 timestamp, bool isActive, uint256 expiry)[] asks)",
  "function getPlatformMaximumPricePerUnit() view returns (uint256)",
  "function getPlatformMinimumPricePerUnit() view returns (uint256)",
  "function getPlatformPriceRange() view returns (uint256, uint256)",
  "function getPriceRange() view returns (uint256, uint256)",
  "function getSellerListings(address seller, uint256 offset, uint256 limit) view returns (tuple(uint256 listingId, address seller, bytes32 energyType, bytes32 userId, uint256 quantity, uint256 pricePerUnit, bytes32 location, uint256 minPurchaseAmount, uint256 maxPurchaseAmount, uint8 status)[] paginatedListings, uint256 totalCount)",
  "function getUserEnergyUsage(address user) view returns (uint256)",
  "function grantRole(bytes32 role, address account, bool status)",
  "function initialize(address _energySharingContractAddress, address _buyerDemandContractAddress, address _orderBookAddress, address _priceOracleAddress)",
  "function initiateDemandTrade(uint256 demandId, uint256 energyUnits) returns (uint256 tradeId)",
  "function initiateListingTrade(uint256 listingId, uint256 energyUnitsToPurchase) returns (uint256 tradeId)",
  "function orderBook() view returns (address)",
  "function owner() view returns (address)",
  "function pauseDemand(uint256 demandId)",
  "function pauseListing(uint256 listingId)",
  "function priceOracle() view returns (address)",
  "function renounceOwnership()",
  "function resumeDemand(uint256 demandId)",
  "function resumeListing(uint256 listingId)",
  "function roles(bytes32, address) view returns (bool)",
  "function setUserEnergyUsage(address user, uint256 usageInKW)",
  "function settleTrade(bool isListing, address partyA, uint256 tradeId)",
  "function transferOwnership(address newOwner)",
  "function updateContractAddresses(address _newEnergySharingContractAddress, address _newBuyerDemandContractAddress)",
  "function updateDemand(uint256 demandId, uint256 newPricePerUnit, uint256 newMinimumSellAmount, uint256 newMaximumSellAmount)",
  "function updateListing(uint256 listingId, uint256 newPricePerUnit, uint256 newMinimumPurchaseAmount, uint256 newMaximumPurchaseAmount)",
];

// Helper functions for bytes32 conversion
function stringToBytes32(str: string): string {
  return ethers.encodeBytes32String(str);
}

function bytes32ToString(bytes32: string): string {
  return ethers.decodeBytes32String(bytes32);
}

class EnergyTradingIntegration {
  static errorMap: Record<string, ErrorInfo> = {
    InvalidPriceRange: {
      code: "INVALID_PRICE_RANGE",
      message:
        "Price must be within the allowed range. Check platform price limits.",
    },
    InvalidPurchaseLimits: {
      code: "INVALID_PURCHASE_LIMITS",
      message:
        "Minimum purchase amount must be greater than 0 and less than maximum amount.",
    },
    InvalidAddress: {
      code: "INVALID_ADDRESS",
      message: "Invalid address provided. Address cannot be zero.",
    },
    InvalidPositiveValue: {
      code: "INVALID_POSITIVE_VALUE",
      message: "Value must be greater than zero.",
    },
    MinPriceMustBeLessThanMax: {
      code: "INVALID_PRICE_LIMITS",
      message: "Minimum price must be less than maximum price.",
    },
    NotListingOwner: {
      code: "NOT_LISTING_OWNER",
      message: "Only the listing owner can perform this action.",
    },
    NotTradeBuyer: {
      code: "NOT_TRADE_BUYER",
      message: "Only the buyer of this trade can perform this action.",
    },
    NotTradeSeller: {
      code: "NOT_TRADE_SELLER",
      message: "Only the seller of this trade can perform this action.",
    },
    NotRouter: {
      code: "NOT_ROUTER",
      message: "This function can only be called through the router contract.",
    },
    InsufficientExcessEnergy: {
      code: "INSUFFICIENT_EXCESS_ENERGY",
      message: "Insufficient excess energy balance to create this listing.",
    },
    SellerNotRegistered: {
      code: "SELLER_NOT_REGISTERED",
      message: "Seller must be registered in the business plan system first.",
    },
    InvalidListing: {
      code: "INVALID_LISTING",
      message: "Listing does not exist or is not active.",
    },
    BuyerNotRegistered: {
      code: "BUYER_NOT_REGISTERED",
      message: "Buyer must be registered in the business plan system first.",
    },
    InsufficientCapacity: {
      code: "INSUFFICIENT_CAPACITY",
      message: "Insufficient energy capacity available for this trade.",
    },
    InvalidTrade: {
      code: "INVALID_TRADE",
      message:
        "Trade does not exist or is not in a valid state for this operation.",
    },
    InvalidTradeState: {
      code: "INVALID_TRADE_STATE",
      message: "Trade is not in the correct state for this operation.",
    },
    ListingNotActive: {
      code: "LISTING_NOT_ACTIVE",
      message: "Listing must be active to perform this operation.",
    },
    ListingNotPaused: {
      code: "LISTING_NOT_PAUSED",
      message: "Listing must be paused to resume it.",
    },
    ListingNotFound: {
      code: "LISTING_NOT_FOUND",
      message: "Listing with the specified ID does not exist.",
    },
    BuyerUsageExceedsSellerExcess: {
      code: "BUYER_USAGE_EXCEEDS_SELLER_EXCESS",
      message: "Buyer energy usage exceeds seller excess energy capacity.",
    },
    InvalidSellLimits: {
      code: "INVALID_SELL_LIMITS",
      message:
        "Sell limits are invalid. Minimum must be > 0, less than maximum, and maximum must not exceed quantity.",
    },
    NotDemandOwner: {
      code: "NOT_DEMAND_OWNER",
      message: "Only the demand owner can perform this action.",
    },
    DemandNotFound: {
      code: "DEMAND_NOT_FOUND",
      message: "Demand with the specified ID does not exist.",
    },
    DemandNotActive: {
      code: "DEMAND_NOT_ACTIVE",
      message: "Demand must be active to perform this operation.",
    },
    DemandNotPaused: {
      code: "DEMAND_NOT_PAUSED",
      message: "Demand must be paused to resume it.",
    },
    TradeNotFound: {
      code: "TRADE_NOT_FOUND",
      message: "Trade with the specified ID does not exist.",
    },
    TradeNotPending: {
      code: "TRADE_NOT_PENDING",
      message: "Trade is not in a pending state.",
    },
    PaymentNotConfirmed: {
      code: "PAYMENT_NOT_CONFIRMED",
      message: "Payment for this trade has not been confirmed.",
    },
    InvalidDemand: {
      code: "INVALID_DEMAND",
      message: "Demand does not exist or is not active.",
    },
    BelowMinSell: {
      code: "BELOW_MIN_SELL",
      message: "Sell amount is below the minimum allowed for this demand.",
    },
    ExceedsMaxSell: {
      code: "EXCEEDS_MAX_SELL",
      message: "Sell amount exceeds the maximum allowed for this demand.",
    },
    InsufficientDemandedUnits: {
      code: "INSUFFICIENT_DEMANDED_UNITS",
      message: "Not enough demanded units available for this trade.",
    },
    MaxSellExceedsQuantity: {
      code: "MAX_SELL_EXCEEDS_QUANTITY",
      message: "Maximum sell amount exceeds the total demand quantity.",
    },
    InvalidMinSellAmount: {
      code: "INVALID_MIN_SELL_AMOUNT",
      message: "Minimum sell amount is invalid for this demand.",
    },
  };

  private routerContract: any;
  private errorInterface: any;
  private signer: any;
  private role: "seller" | "buyer" | "deployer";

  constructor(role: "seller" | "buyer" | "deployer" = "deployer") {
    this.role = role;
  }

  async init() {
    const contracts = await getContracts();
    let signerToUse;
    if (this.role === "seller") signerToUse = contracts.seller1;
    else if (this.role === "buyer") signerToUse = contracts.buyer1;
    else signerToUse = contracts.signer;
    this.signer = signerToUse;
    // Reconnect contract with correct signer
    this.routerContract =
      contracts.energySharingContractInstance.connect(signerToUse);
    this.errorInterface = new ethers.Interface(ERROR_ABI);
    return this;
  }

  /**
   * Decode custom errors from transaction failure
   */
  private decodeError(error: any): ErrorInfo {
    console.log("Raw error:", error);

    // Check if it's a custom error with data
    if (error.data) {
      try {
        const decodedError = this.errorInterface.parseError(error.data);
        if (decodedError) {
          return this.mapCustomError(decodedError.name);
        }
      } catch (decodeErr) {
        console.log("Failed to decode custom error:", decodeErr);
      }
    }

    // Check for revert reason in various error formats
    if (error.reason) {
      return this.mapRevertReason(error.reason);
    }

    // Check nested error structures
    if (error.error?.data) {
      try {
        const decodedError = this.errorInterface.parseError(error.error.data);
        if (decodedError) {
          return this.mapCustomError(decodedError.name);
        }
      } catch (decodeErr) {
        console.log("Failed to decode nested error:", decodeErr);
      }
    }

    // Check for transaction execution reverted
    if (error.message?.includes("execution reverted")) {
      // Try to extract custom error from message
      const customErrorMatch = error.message.match(/execution reverted: (.+)/);
      if (customErrorMatch) {
        // Try mapping, but if not found, return as a revert error
        const mapped = this.mapRevertReason(customErrorMatch[1]);
        if (mapped.code === "REVERT_ERROR") {
          // Use the revert reason as the main message
          return {
            code: "REVERT_ERROR",
            message: customErrorMatch[1],
            details: error.message,
          };
        }
        return mapped;
      }
    }

    // If error.details or error.message contains a revert reason, use it
    const revertReason =
      typeof error.details === "string" && error.details.match(/failed: (.+)/)
        ? error.details.match(/failed: (.+)/)[1]
        : null;
    if (revertReason) {
      return {
        code: "REVERT_ERROR",
        message: revertReason,
        details: error.details,
      };
    }

    // Try to match revert reason string to known custom error messages
    const detailsString = error.details || error.message || "";
    const revertMsg = detailsString.replace(/^.*failed: /, "").trim();
    for (const value of Object.values(EnergyTradingIntegration.errorMap)) {
      if (
        value.message === revertMsg ||
        revertMsg.toLowerCase().includes(value.message.toLowerCase())
      ) {
        return {
          code: value.code,
          message: value.message,
          details: detailsString,
        };
      }
    }

    // Default error mapping
    if (
      detailsString.includes("failed:") ||
      detailsString.toLowerCase().includes("revert") ||
      detailsString.toLowerCase().includes("must be")
    ) {
      return {
        code: "REVERT_ERROR",
        message: detailsString,
        details: error.message || error,
      };
    }
    return {
      code: "UNKNOWN_ERROR",
      message:
        detailsString ||
        "An unknown error occurred during transaction execution",
      details: error.message || error,
    };
  }

  /**
   * Map custom error names to user-friendly messages
   */
  private mapCustomError(errorName: string): ErrorInfo {
    const errorMap = EnergyTradingIntegration.errorMap;
    return (
      errorMap[errorName] || {
        code: "CUSTOM_ERROR",
        message: `Custom error: ${errorName}`,
        details: errorName,
      }
    );
  }

  /**
   * Map revert reasons to user-friendly messages
   */
  private mapRevertReason(reason: string): ErrorInfo {
    const reasonMap: Record<string, ErrorInfo> = {
      INVALID_ENERGY_SHARING_CONTRACT: {
        code: "INVALID_CONTRACT",
        message: "Invalid energy sharing contract address.",
      },
      INVALID_BUYER_DEMAND_CONTRACT: {
        code: "INVALID_CONTRACT",
        message: "Invalid buyer demand contract address.",
      },
      INVALID_USER: {
        code: "INVALID_USER",
        message: "Invalid user address provided.",
      },
      INVALID_ENERGY_AMOUNT: {
        code: "INVALID_AMOUNT",
        message: "Energy amount must be greater than zero.",
      },
      INVALID_USAGE_AMOUNT: {
        code: "INVALID_AMOUNT",
        message: "Usage amount must be greater than zero.",
      },
      "Must have role": {
        code: "INSUFFICIENT_PERMISSIONS",
        message: "Insufficient permissions to perform this action.",
      },
    };

    return (
      reasonMap[reason] || {
        code: "REVERT_ERROR",
        message: reason || "Transaction was reverted",
        details: reason,
      }
    );
  }

  // Refactored executeTransaction to use callStatic for error simulation
  private async executeTransaction(
    methodName: string,
    args: any[],
    operationName: string
  ): Promise<any> {
    try {
      // Try callStatic first to catch errors before sending
      if (
        this.routerContract.callStatic &&
        this.routerContract.callStatic[methodName]
      ) {
        try {
          await this.routerContract.callStatic[methodName](...args);
        } catch (staticError: any) {
          const errorInfo = this.decodeError(staticError);
          throw new EnergyTradingError(
            errorInfo.code,
            `${operationName} failed: ${errorInfo.message}`,
            errorInfo.details
          );
        }
      }
      // Proceed with the real transaction
      const tx = await this.routerContract[methodName](...args);
      const receipt = await tx.wait();
      return receipt;
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `${operationName} failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  // Public methods for interacting with the energy trading system

  // Update createListing to use the new executeTransaction signature
  async createListing(params: CreateListingParams): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    const energyTypeBytes32 = stringToBytes32(params.energyType);
    const locationBytes32 = stringToBytes32(params.location);
    return this.executeTransaction(
      "createListing",
      [
        energyTypeBytes32,
        params.excessEnergyKW,
        params.pricePerUnit,
        locationBytes32,
        params.minPurchaseAmount,
        params.maxPurchaseAmount,
      ],
      "Create Listing"
    );
  }

  // Update all other methods that use executeTransaction in a similar way
  async initiateListingTrade(
    listingId: number,
    energyUnits: number
  ): Promise<number> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    const receipt = await this.executeTransaction(
      "initiateListingTrade",
      [listingId, energyUnits],
      "Initiate Listing Trade"
    );
    // Extract trade ID from events
    const event = receipt.logs.find((log: any) => {
      try {
        const parsed = this.routerContract.interface.parseLog(log);
        return parsed?.name === "ListingTradeInitiated";
      } catch {
        return false;
      }
    });
    if (event) {
      const parsedEvent = this.routerContract.interface.parseLog(event);
      return parsedEvent?.args.tradeId.toNumber();
    }
    throw new EnergyTradingError(
      "TRADE_ID_NOT_FOUND",
      "Could not extract trade ID from transaction"
    );
  }

  async confirmListingTradePayment(tradeId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "confirmListingTradePayment",
      [tradeId],
      "Confirm Listing Trade Payment"
    );
  }

  async completeListingTrade(tradeId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "completeListingTrade",
      [tradeId],
      "Complete Listing Trade"
    );
  }

  async cancelListingTrade(tradeId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "cancelListingTrade",
      [tradeId],
      "Cancel Listing Trade"
    );
  }

  async updateListing(
    listingId: number,
    newPricePerUnit: number,
    newMinPurchaseAmount: number,
    newMaxPurchaseAmount: number
  ): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "updateListing",
      [listingId, newPricePerUnit, newMinPurchaseAmount, newMaxPurchaseAmount],
      "Update Listing"
    );
  }

  async cancelListing(listingId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "cancelListing",
      [listingId],
      "Cancel Listing"
    );
  }

  async pauseListing(listingId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "pauseListing",
      [listingId],
      "Pause Listing"
    );
  }

  async resumeListing(listingId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "resumeListing",
      [listingId],
      "Resume Listing"
    );
  }

  /**
   * Create a listing and register an order atomically via the router
   */
  async createListingWithOrder(params: CreateListingParams): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    const energyTypeBytes32 = stringToBytes32(params.energyType);
    const locationBytes32 = stringToBytes32(params.location);
    return this.executeTransaction(
      "createListingWithOrder",
      [
        energyTypeBytes32,
        params.excessEnergyKW,
        params.pricePerUnit,
        locationBytes32,
        params.minPurchaseAmount,
        params.maxPurchaseAmount,
      ],
      "Create Listing With Order"
    );
  }

  /**
   * Create a demand and register an order atomically via the router
   */
  async createDemandWithOrder(params: {
    energyType: string;
    quantity: number;
    pricePerUnit: number;
    location: string;
    minSellAmount: number;
    maxSellAmount: number;
  }): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    const locationBytes32 = stringToBytes32(params.location);
    return this.executeTransaction(
      "createDemandWithOrder",
      [
        params.energyType,
        params.quantity,
        params.pricePerUnit,
        locationBytes32,
        params.minSellAmount,
        params.maxSellAmount,
      ],
      "Create Demand With Order"
    );
  }

  /**
   * Cancel a listing and its order atomically via the router
   */
  async cancelListingWithOrder(
    listingId: number,
    orderId: number
  ): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "cancelListingWithOrder",
      [listingId, orderId],
      "Cancel Listing With Order"
    );
  }

  /**
   * Cancel a demand and its order atomically via the router
   */
  async cancelDemandWithOrder(demandId: number, orderId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "cancelDemandWithOrder",
      [demandId, orderId],
      "Cancel Demand With Order"
    );
  }

  /**
   * Admin: Credit user excess energy
   */
  async creditUserExcessEnergy(
    seller: string,
    excessEnergyKW: number
  ): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "creditUserExcessEnergy",
      [seller, excessEnergyKW],
      "Credit User Excess Energy"
    );
  }

  /**
   * Admin: Set user energy usage
   */
  async setUserEnergyUsage(user: string, usageInKW: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "setUserEnergyUsage",
      [user, usageInKW],
      "Set User Energy Usage"
    );
  }

  /**
   * Admin: Consume energy units for a user
   */
  async consumeEnergyUnits(user: string, newBalance: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "consumeEnergyUnits",
      [user, newBalance],
      "Consume Energy Units"
    );
  }

  // Read-only methods (no transaction required)

  /**
   * Get a single seller listing by listingId
   */
  // async getSellerListing(listingId: number): Promise<{
  //   listingId: number;
  //   seller: string;
  //   energyType: string;
  //   userId: string;
  //   quantity: number;
  //   pricePerUnit: number;
  //   location: string;
  //   minPurchaseAmount: number;
  //   maxPurchaseAmount: number;
  //   status: number;
  // }> {
  //   try {
  //     const result = await this.routerContract.getSellerListing(listingId);
  //     return {
  //       listingId: result[0].toString(),
  //       seller: result[1],
  //       energyType: bytes32ToString(result[2]),
  //       userId: bytes32ToString(result[3]),
  //       quantity: result[4].toNumber(),
  //       pricePerUnit: result[5].toNumber(),
  //       location: bytes32ToString(result[6]),
  //       minPurchaseAmount: result[7].toNumber(),
  //       maxPurchaseAmount: result[8].toNumber(),
  //       status: result[9],
  //     };
  //   } catch (error: any) {
  //     const errorInfo = this.decodeError(error);
  //     throw new EnergyTradingError(
  //       errorInfo.code,
  //       `Get Seller Listing failed: ${errorInfo.message}`,
  //       errorInfo.details
  //     );
  //   }
  // }
  // async getSellerListing(listingId: number): Promise<{
  //   listingId: number;
  //   seller: string;
  //   energyType: string;
  //   userId: string;
  //   quantity: number;
  //   pricePerUnit: number;
  //   location: string;
  //   minPurchaseAmount: number;
  //   maxPurchaseAmount: number;
  //   status: number;
  // }> {
  //   try {
  //     const result = await this.routerContract.getSellerListing(listingId);
      
  //     // Debug logging to see what we're getting
  //     console.log('Raw contract result:', result);
  //     console.log('Result types:', result.map((item: any, index: number) => `${index}: ${typeof item} - ${item}`));
      
  //     return {
  //       listingId: this.safeBigIntToNumber(result[0]),
  //       seller: result[1],
  //       energyType: bytes32ToString(result[2]),
  //       userId: bytes32ToString(result[3]),
  //       quantity: this.safeBigIntToNumber(result[4]),
  //       pricePerUnit: this.safeBigIntToNumber(result[5]),
  //       location: bytes32ToString(result[6]),
  //       minPurchaseAmount: this.safeBigIntToNumber(result[7]),
  //       maxPurchaseAmount: this.safeBigIntToNumber(result[8]),
  //       status: this.safeBigIntToNumber(result[9]),
  //     };
  //   } catch (error: any) {
  //     console.error('Full error object:', error);
  //     const errorInfo = this.decodeError(error);
  //     throw new EnergyTradingError(
  //       errorInfo.code,
  //       `Get Seller Listing failed: ${errorInfo.message}`,
  //       errorInfo.details
  //     );
  //   }
  // }
async getSellerListing(listingId: number): Promise<{
  listingId: number;
  seller: string;
  energyType: string;
  userId: string;
  quantity: number;
  pricePerUnit: number;
  location: string;
  minPurchaseAmount: number;
  maxPurchaseAmount: number;
  status: number;
}> {
  try {
    const result = await this.routerContract.getSellerListing(listingId);
    
    // Debug logging to see what we're getting
    console.log('Raw contract result:', result);
    console.log('Result types:', result.map((item: any, index: number) => `${index}: ${typeof item} - ${item}`));
    
    // Parse each field correctly based on its expected type
    return {
      listingId: this.safeBigIntToNumber(result[0]),
      seller: result[1], // This is an address, keep as string
      energyType: this.safeBytes32ToString(result[2]), // bytes32 to string
      userId: this.safeBytes32ToString(result[3]), // bytes32 to string
      quantity: this.safeBigIntToNumber(result[4]),
      pricePerUnit: this.safeBigIntToNumber(result[5]),
      location: this.safeBytes32ToString(result[6]), // bytes32 to string
      minPurchaseAmount: this.safeBigIntToNumber(result[7]),
      maxPurchaseAmount: this.safeBigIntToNumber(result[8]),
      status: this.safeBigIntToNumber(result[9]),
    };
  } catch (error: any) {
    console.error('Full error object:', error);
    const errorInfo = this.decodeError(error);
    throw new EnergyTradingError(
      errorInfo.code,
      `Get Seller Listing failed: ${errorInfo.message}`,
      errorInfo.details
    );
  }
}

// Add this helper method for safe bytes32 to string conversion
private safeBytes32ToString(bytes32Value: any): string {
  try {
    // If it's already a string and doesn't look like hex, return as is
    if (typeof bytes32Value === 'string' && !bytes32Value.startsWith('0x')) {
      return bytes32Value;
    }
    
    // If it's hex or bytes32, try to decode it
    if (bytes32Value && bytes32Value !== '0x') {
      const decoded = bytes32ToString(bytes32Value);
      // Remove null bytes and trim
      return decoded.replace(/\0/g, '').trim();
    }
    
    return '';
  } catch (error) {
    console.error('Error converting bytes32 to string:', bytes32Value, error);
    // If decoding fails, try to return a string representation
    return String(bytes32Value || '');
  }
}

// Update your existing safeBigIntToNumber to be more robust
private safeBigIntToNumber(value: any): number {
  try {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return 0;
    }
    
    // If it's already a number, return it
    if (typeof value === 'number') {
      return value;
    }
    
    // If it's a BigInt
    if (typeof value === 'bigint') {
      return Number(value);
    }
    
    // If it's a string
    if (typeof value === 'string') {
      // If it's hex, convert to BigInt first
      if (value.startsWith('0x')) {
        // Check if it's a valid hex string for a number
        if (value.length <= 18) { // Reasonable size for a number
          return Number(BigInt(value));
        } else {
          // Too large for a number, might be an address or bytes32
          console.warn('Hex string too long for number conversion:', value);
          return 0;
        }
      }
      
      // If it's a decimal string
      if (/^\d+$/.test(value)) {
        return Number(value);
      }
      
      // If it contains non-numeric characters, it's probably not a number
      console.warn('String contains non-numeric characters:', value);
      return 0;
    }
    
    // For ethers.js BigNumber objects
    if (value && typeof value.toString === 'function') {
      const strVal = value.toString();
      return this.safeBigIntToNumber(strVal);
    }
    
    // Fallback: try to convert directly
    const num = Number(value);
    if (isNaN(num)) {
      console.warn('Cannot convert to number:', value);
      return 0;
    }
    return num;
  } catch (error) {
    console.error(`Error converting value to number: ${value}`, error);
    return 0; // Return 0 as fallback instead of throwing
  }
}
  // Helper method to safely convert BigInt/hex strings to numbers
  // private safeBigIntToNumber(value: any): number {
  //   try {
  //     // If it's already a number, return it
  //     if (typeof value === 'number') {
  //       return value;
  //     }
      
  //     // If it's a string that looks like hex, convert to BigInt first
  //     if (typeof value === 'string') {
  //       if (value.startsWith('0x')) {
  //         return Number(BigInt(value));
  //       }
  //       // If it's a decimal string
  //       return Number(value);
  //     }
      
  //     // If it's already a BigInt
  //     if (typeof value === 'bigint') {
  //       return Number(value);
  //     }
      
  //     // Fallback: try to convert directly
  //     return Number(value);
  //   } catch (error) {
  //     console.error(`Error converting value to number: ${value}`, error);
  //     throw new Error(`Cannot convert ${value} to number`);
  //   }
  // }

  /**
   * Get active listings with pagination
   */
  async getActiveListings(
    offset: number = 0,
    limit: number = 10
  ): Promise<any> {
    try {
      const [listings, totalCount] =
        await this.routerContract.getActiveListings(offset, limit);

      return {
        listings: listings.map((listing: any) => ({
          listingId: listing.listingId.toString(),
          seller: listing.seller,
          energyType: bytes32ToString(listing.energyType),
          userId: bytes32ToString(listing.userId),
          quantity: listing.quantity.toString(),
          pricePerUnit: listing.pricePerUnit.toString(),
          location: bytes32ToString(listing.location),
          minPurchaseAmount: listing.minPurchaseAmount.toString(),
          maxPurchaseAmount: listing.maxPurchaseAmount.toString(),
          status: listing.status,
        })),
        totalCount: totalCount.toString(),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Active Listings failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get user's energy unit balance
   */
  async getEnergyUnitBalance(userAddress: string): Promise<number> {
    try {
      const balance = await this.routerContract.getEnergyUnitBalance(
        userAddress
      );
      return typeof balance?.toNumber === 'function' ? balance.toNumber() : Number(balance);
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Energy Unit Balance failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get seller's excess energy balance
   */
  async getExcessEnergyBalance(sellerAddress: string): Promise<number> {
    try {
      const balance = await this.routerContract.getExcessEnergyBalance(
        sellerAddress
      );
      return balance.toString();

    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Excess Energy Balance failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get platform price limits
   */
  async getPriceLimits(): Promise<{ min: number; max: number }> {
    try {
      const [min, max] = await this.routerContract.getPriceLimits();
      return {
        min: min.toNumber(),
        max: max.toNumber(),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Price Limits failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Grant or revoke a role to an account (admin only)
   */
  async grantRole(
    role: string,
    account: string,
    status: boolean
  ): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction(
      "grantRole",
      [role, account, status],
      "Grant Role"
    );
  }

  /**
   * Update contract addresses (admin only)
   */
  async updateContractAddresses(
    newEnergySharingContract: string,
    newBuyerDemandContract: string
  ): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction(
      "updateContractAddresses",
      [newEnergySharingContract, newBuyerDemandContract],
      "Update Contract Addresses"
    );
  }

  /**
   * Update a demand
   */
  async updateDemand(
    demandId: number,
    newPricePerUnit: number,
    newMinSellAmount: number,
    newMaxSellAmount: number
  ): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction(
      "updateDemand",
      [demandId, newPricePerUnit, newMinSellAmount, newMaxSellAmount],
      "Update Demand"
    );
  }

  /**
   * Pause a demand
   */
  async pauseDemand(demandId: number): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction("pauseDemand", [demandId], "Pause Demand");
  }

  /**
   * Resume a demand
   */
  async resumeDemand(demandId: number): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction("resumeDemand", [demandId], "Resume Demand");
  }

  /**
   * Get listing price per unit
   */
  async getListingPricePerUnit(listingId: number): Promise<number> {
    try {
      const price = await this.routerContract.getListingPricePerUnit(listingId);
      return price.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Listing Price Per Unit failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Initiate a demand trade and return the tradeId
   */
  async initiateDemandTrade(demandId: number, energyUnits: number): Promise<number> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    const receipt = await this.executeTransaction(
      "initiateDemandTrade",
      [demandId, energyUnits],
      "Initiate Demand Trade"
    );
    // Extract trade ID from events
    const event = receipt.logs.find((log: any) => {
      try {
        const parsed = this.routerContract.interface.parseLog(log);
        return parsed?.name === "DemandTradeInitiated";
      } catch {
        return false;
      }
    });
    if (event) {
      const parsedEvent = this.routerContract.interface.parseLog(event);
      return parsedEvent?.args.tradeId.toNumber();
    }
    throw new EnergyTradingError(
      "TRADE_ID_NOT_FOUND",
      "Could not extract trade ID from transaction"
    );
  }
 

  /**
   * Get demand price per unit
   */
  async getDemandPricePerUnit(demandId: number): Promise<number> {
    try {
      const price = await this.routerContract.getDemandPricePerUnit(demandId);
      return price.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Demand Price Per Unit failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get active demands with pagination
   */
  async getActiveDemands(offset: number = 0, limit: number = 20): Promise<any> {
    try {
      const [demands, totalCount] = await this.routerContract.getActiveDemands(
        offset,
        limit
      );
      return {
        demands,
        totalCount: totalCount.toString(),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Active Demands failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get seller's listings
   */
  async getSellerListings(
    seller: string,
    offset: number = 0,
    limit: number = 10
  ): Promise<any> {
    try {
      const [listings, totalCount] =
        await this.routerContract.getSellerListings(seller, offset, limit);
      return {
        // listings,
        totalCount: typeof totalCount?.toString === 'function' ? totalCount.toString() :Number(totalCount),
        listings: listings.map((listing: any) => ({
          listingId: listing.listingId.toString(),
          seller: listing.seller,
          energyType: bytes32ToString(listing.energyType),
          userId: bytes32ToString(listing.userId),
          quantity: listing.quantity.toString(),
          pricePerUnit: listing.pricePerUnit.toString(),
          location: bytes32ToString(listing.location),
          minPurchaseAmount: listing.minPurchaseAmount.toString(),
          maxPurchaseAmount: listing.maxPurchaseAmount.toString(),
          status: listing.status,
        })),

      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Seller Listings failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get all listings
   */
  async getAllListings(offset: number = 0, limit: number = 10): Promise<any> {
    try {
      const [listings, totalCount] = await this.routerContract.getAllListings(
        offset,
        limit
      );
      return {
        listings,
        totalCount: typeof totalCount?.toString === 'function' ? totalCount.toString() : Number(totalCount),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get All Listings failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get buyer's demands
   */
  async getBuyerDemands(
    buyer: string,
    offset: number = 0,
    limit: number = 10
  ): Promise<any> {
    try {
      const [demands, totalCount] = await this.routerContract.getBuyerDemands(
        buyer,
        offset,
        limit
      );
      return {
        demands,
        totalCount: typeof totalCount?.toString === 'function' ? totalCount.toString() : Number(totalCount),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Buyer Demands failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get delivered energy units for a buyer
   */
  async getDeliveredEnergyUnits(buyer: string): Promise<number> {
    try {
      const units = await this.routerContract.getDeliveredEnergyUnits(buyer);
      return units.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Delivered Energy Units failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get platform minimum price per unit
   */
  async getPlatformMinimumPricePerUnit(): Promise<number> {
    try {
      const min = await this.routerContract.getPlatformMinimumPricePerUnit();
      return min.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Platform Minimum Price Per Unit failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get platform maximum price per unit
   */
  async getPlatformMaximumPricePerUnit(): Promise<number> {
    try {
      const max = await this.routerContract.getPlatformMaximumPricePerUnit();
      return max.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Platform Maximum Price Per Unit failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get platform price range
   */
  async getPlatformPriceRange(): Promise<{ min: number; max: number }> {
    try {
      const [min, max] = await this.routerContract.getPlatformPriceRange();
      return { min: min.toString(), max: max.toString() };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Platform Price Range failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get energy listing trade count
   */
  async getEnergyListingTradeCount(): Promise<number> {
    try {
      const count = await this.routerContract.getEnergyListingTradeCount();
      return count.toString();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Energy Listing Trade Count failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get demand trade count
   */
  async getDemandTradeCount(): Promise<number> {
    try {
      const count = await this.routerContract.getDemandTradeCount();
      return count.toString();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Demand Trade Count failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get counters (listing and trade)
   */
  async getCounters(): Promise<{
    listingCounter: number;
    tradeCounter: number;
  }> {
    try {
      const [listingCounter, tradeCounter] =
        await this.routerContract.getCounters();
      return {
        listingCounter: listingCounter.toString(),
        tradeCounter: tradeCounter.toString(),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Counters failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get demand counters (demand and trade)
   */
  async getDemandCounters(): Promise<{
    demandCounter: number;
    tradeCounter: number;
  }> {
    try {
      const [demandCounter, tradeCounter] =
        await this.routerContract.getDemandCounters();
      return {
        demandCounter: demandCounter.toNumber(),
        tradeCounter: tradeCounter.toNumber(),
      };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Demand Counters failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get order book for a location
   */
  async getOrderBook(location: string): Promise<any> {
    try {
      const locationBytes32 = stringToBytes32(location);
      const [bids, asks] = await this.routerContract.getOrderBook(
        locationBytes32
      );
      return { bids, asks };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Order Book failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get minimum price from oracle
   */
  async getMinPrice(): Promise<number> {
    try {
      const min = await this.routerContract.getMinPrice();
      return min.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Min Price failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get maximum price from oracle
   */
  async getMaxPrice(): Promise<number> {
    try {
      const max = await this.routerContract.getMaxPrice();
      return max.toNumber();
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Max Price failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Get price range from oracle
   */
  async getPriceRange(): Promise<{ min: number; max: number }> {
    try {
      const [min, max] = await this.routerContract.getPriceRange();
      return { min: min.toNumber(), max: max.toNumber() };
    } catch (error: any) {
      const errorInfo = this.decodeError(error);
      throw new EnergyTradingError(
        errorInfo.code,
        `Get Price Range failed: ${errorInfo.message}`,
        errorInfo.details
      );
    }
  }

  /**
   * Settle a trade (admin only)
   */
  async settleTrade(
    isListing: boolean,
    partyA: string,
    tradeId: number
  ): Promise<any> {
    if (!this.signer)
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    return this.executeTransaction(
      "settleTrade",
      [isListing, partyA, tradeId],
      "Settle Trade"
    );
  }

  async confirmDemandTradePayment(tradeId: number): Promise<any> {
    if (!this.signer) {
      throw new EnergyTradingError(
        "NO_SIGNER",
        "Signer required for this operation"
      );
    }
    return this.executeTransaction(
      "confirmDemandTradePayment",
      [tradeId],
      "Confirm Demand Trade Payment"
    );
  }
}

export {
  EnergyTradingIntegration,
  EnergyTradingError,
  type CreateListingParams,
  type ErrorInfo,
  stringToBytes32,
  bytes32ToString,
  ROUTER_ABI,
  ERROR_ABI,
};
 
